import React, { useEffect, useState } from "react";
import { CheckSquare, CalendarPlus, Heart, Ban } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDayLong, fmtRange, fmtDur } from "@/lib/time";

const PROJECTS = ["Payfit", "360Learning", "Opera Energie", "Livre", "Spectacle", "Administratif", "Contenu", "Autre"];
const PRIORITIES = [["URGENT", "Urgent"], ["IMPORTANT", "Important"], ["QUICK_WIN", "Quick win"], ["THIS_WEEK", "Cette semaine"], ["CAN_WAIT", "Peut attendre"]];
const inputCls = "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm";

// Composer shown after click / drag-select on an empty slot: Tâche · Événement · Personnel · Bloquer
export default function SlotComposer({ open, range, onClose, onCreateTask, onCreateEvent, onQuickEvent }) {
  const [mode, setMode] = useState("task");
  const [title, setTitle] = useState("");
  const [project, setProject] = useState("Autre");
  const [priority, setPriority] = useState("THIS_WEEK");
  const [deadline, setDeadline] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setMode("task"); setTitle(""); setProject("Autre"); setPriority("THIS_WEEK"); setDeadline(""); } }, [open]);
  if (!range) return null;
  const dur = Math.round((range.end - range.start) / 60000);

  const submit = async (e) => {
    e?.preventDefault();
    if (!title.trim() && mode !== "block") return;
    setSaving(true);
    try {
      if (mode === "task") await onCreateTask({ title: title.trim(), project, priority, deadline: deadline || null, start: range.start, end: range.end });
      else if (mode === "event") { onCreateEvent(title.trim(), range); }
      else if (mode === "personal") await onQuickEvent({ title: title.trim() || "Temps personnel", category: "PERSONAL", transparency: "busy" }, range);
      else await onQuickEvent({ title: title.trim() || "Occupé", category: "BUSY", transparency: "busy" }, range);
      onClose();
    } finally { setSaving(false); }
  };

  const Tab = ({ id, icon: Icon, label }) => (
    <button type="button" data-testid={`composer-mode-${id}`} onClick={() => setMode(id)}
      className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-medium transition-colors ${mode === id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="slot-composer">
        <DialogHeader>
          <DialogTitle className="font-display capitalize">{fmtDayLong(range.start)}</DialogTitle>
          <div className="font-mono text-xs text-muted-foreground">{fmtRange(range.start, range.end)} · {fmtDur(dur)}</div>
        </DialogHeader>
        <div className="flex gap-1 rounded-xl border border-border p-1">
          <Tab id="task" icon={CheckSquare} label="Tâche" /><Tab id="event" icon={CalendarPlus} label="Événement" />
          <Tab id="personal" icon={Heart} label="Personnel" /><Tab id="block" icon={Ban} label="Bloquer" />
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input autoFocus data-testid="composer-title" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder={mode === "task" ? "Quoi ? (ex : Écrire script PayFit)" : mode === "event" ? "Titre de l'événement" : mode === "personal" ? "Ex : Golf, Déjeuner famille…" : "Titre (optionnel)"}
            className={`${inputCls} text-base`} />
          {mode === "task" && (
            <div className="grid grid-cols-3 gap-2">
              <select data-testid="composer-project" value={project} onChange={(e) => setProject(e.target.value)} className={inputCls}>{PROJECTS.map((p) => <option key={p}>{p}</option>)}</select>
              <select data-testid="composer-priority" value={priority} onChange={(e) => setPriority(e.target.value)} className={inputCls}>{PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
              <input type="date" data-testid="composer-deadline" value={deadline} onChange={(e) => setDeadline(e.target.value)} className={inputCls} title="Échéance (optionnel)" />
            </div>
          )}
          <div className="text-xs text-muted-foreground">
            {mode === "task" && "Crée une tâche planifiée sur ce créneau (bloc de tâche, déplaçable et redimensionnable)."}
            {mode === "event" && "Ouvre le formulaire complet (lieu, notes, récurrence, occupé / libre)."}
            {mode === "personal" && "Événement personnel OCCUPÉ — AIRTIME n'y planifiera jamais de travail."}
            {mode === "block" && "Bloque simplement ce créneau comme indisponible."}
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-full border border-border px-4 py-2 text-sm hover:bg-accent">Annuler</button>
            <button type="submit" data-testid="composer-submit" disabled={saving || (mode !== "block" && !title.trim())}
              className="rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground font-medium disabled:opacity-50">
              {mode === "task" ? "Créer la tâche" : mode === "event" ? "Continuer" : mode === "personal" ? "Bloquer (personnel)" : "Bloquer ce créneau"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
